import { useState } from 'react';
import type { Credential } from '@dental-staffing-platform/shared';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Pagination,
  Row,
  Select,
  SkeletonList,
  Stack,
} from '@dental-staffing-platform/ui';
import { adminApi } from '../lib/admin-client.js';
import { useAsync } from '../lib/useAsync.js';
import { credentialTone, humanize } from '../lib/format.js';

const FILTERS = ['pending', 'approved', 'rejected', 'all'];

export function CredentialReview() {
  const [filter, setFilter] = useState('pending');
  const [type, setType] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const { data, loading, error, reload } = useAsync(
    () =>
      adminApi.credentials.page({
        page,
        pageSize: 20,
        ...(filter !== 'all' ? { status: filter as Credential['reviewStatus'] } : {}),
        ...(type !== 'all' ? { type: type as Credential['type'] } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
      }),
    [filter, type, search, page]
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const decide = async (id: string, decision: 'approved' | 'rejected', reason?: string) => {
    setBusy(id);
    setActionError(null);
    try {
      await adminApi.credentials.review(id, decision, reason);
      setRejectingId(null);
      setRejectionReason('');
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not update credential');
    } finally {
      setBusy(null);
    }
  };

  const viewDoc = async (id: string) => {
    setActionError(null);
    try {
      window.open(await adminApi.credentials.documentUrl(id), '_blank', 'noopener');
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not open document');
    }
  };

  return (
    <div>
      <PageHeader
        title="Credential review"
        subtitle="Verify professional licenses and certifications"
      />

      <Card style={{ marginBottom: 16 }}>
        <Row gap={12} style={{ alignItems: 'end', flexWrap: 'wrap' }}>
          <Field label="Search">
            <Input
              type="search"
              value={search}
              placeholder="Professional or credential"
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </Field>
          <Field label="Review status">
            <Select
              value={filter}
              onChange={(event) => {
                setFilter(event.target.value);
                setPage(1);
              }}
            >
              {FILTERS.map((value) => (
                <option key={value} value={value}>
                  {value === 'all' ? 'All statuses' : humanize(value)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Credential type">
            <Select
              value={type}
              onChange={(event) => {
                setType(event.target.value);
                setPage(1);
              }}
            >
              <option value="all">All credential types</option>
              <option value="state_dental_license">State dental license</option>
              <option value="cpr_bls">CPR / BLS</option>
              <option value="radiology_xray_cert">Radiology / X-ray</option>
              <option value="hepatitis_b">Hepatitis B</option>
              <option value="liability_insurance">Liability insurance</option>
              <option value="diploma">Diploma</option>
              <option value="other">Other</option>
            </Select>
          </Field>
        </Row>
      </Card>

      {actionError ? (
        <Card style={{ marginBottom: 12 }}>
          <span style={{ color: 'var(--dsp-danger-fg)' }}>{actionError}</span>
        </Card>
      ) : null}

      {loading ? (
        <SkeletonList count={3} />
      ) : error ? (
        <Card>
          <span style={{ color: 'var(--dsp-danger-fg)' }}>Couldn’t load the queue: {error}</span>
        </Card>
      ) : !data || data.items.length === 0 ? (
        <EmptyState
          title="Nothing to review"
          description={
            filter === 'pending'
              ? 'All caught up — no pending credentials.'
              : 'No credentials in this view.'
          }
        />
      ) : (
        <Stack gap={12}>
          {data.items.map((c) => (
            <Card key={c.id}>
              <Row style={{ justifyContent: 'space-between', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700 }}>{c.professionalName}</div>
                  <div className="dsp-muted" style={{ fontSize: '0.88rem', marginTop: 2 }}>
                    {humanize(c.type)}
                    {c.label ? ` · ${c.label}` : ''}
                    {c.issuingState ? ` · ${c.issuingState}` : ''}
                    {c.expiresAt ? ` · exp ${c.expiresAt.slice(0, 10)}` : ''}
                  </div>
                </div>
                <Row gap={8} style={{ flex: 'none' }}>
                  <Badge tone={credentialTone(c.reviewStatus)}>{humanize(c.reviewStatus)}</Badge>
                  {c.documentRef ? (
                    <Button size="sm" variant="secondary" onClick={() => void viewDoc(c.id)}>
                      View
                    </Button>
                  ) : (
                    <span className="dsp-muted" style={{ fontSize: '0.78rem' }}>
                      No file
                    </span>
                  )}
                  {c.reviewStatus === 'pending' ? (
                    <>
                      <Button
                        size="sm"
                        onClick={() => void decide(c.id, 'approved')}
                        disabled={busy === c.id}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => {
                          setRejectingId(c.id);
                          setRejectionReason('');
                          setActionError(null);
                        }}
                        disabled={busy === c.id}
                      >
                        Reject
                      </Button>
                    </>
                  ) : null}
                </Row>
              </Row>
              {rejectingId === c.id ? (
                <div style={{ marginTop: 12 }}>
                  <Field label="What needs to change?">
                    <Input
                      autoFocus
                      value={rejectionReason}
                      placeholder="For example, upload a current license"
                      onChange={(event) => setRejectionReason(event.target.value)}
                    />
                  </Field>
                  <Row gap={8} style={{ marginTop: 10 }}>
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={busy === c.id || !rejectionReason.trim()}
                      onClick={() => void decide(c.id, 'rejected', rejectionReason.trim())}
                    >
                      {busy === c.id ? 'Saving…' : 'Confirm rejection'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setRejectingId(null)}>
                      Keep pending
                    </Button>
                  </Row>
                </div>
              ) : null}
            </Card>
          ))}
          <Pagination {...data} itemLabel="credentials" disabled={loading} onPageChange={setPage} />
        </Stack>
      )}
    </div>
  );
}
